import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, XCircle, Loader2, ExternalLink, Phone, Mail, Building2, FileText } from 'lucide-react';

const ApprovalQueue = ({ onDecisionComplete, currentUser }) => {
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailedAnalysis, setDetailedAnalysis] = useState({});
  const [loadingAnalysis, setLoadingAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const visibleCompanies = filterText.trim()
    ? pendingCompanies.filter(c => {
        const q = filterText.toLowerCase();
        return [c.title, c.city, c.website, c.address].some(v => (v || '').toLowerCase().includes(q));
      })
    : pendingCompanies;

  const fetchPendingCompanies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/companies?status=PENDING`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Fehler beim Laden der Unternehmen');
      const data = await response.json();
      setPendingCompanies(data);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingCompanies();
    // Auto-refresh every 2 minutes (to avoid overriding manual changes)
    const interval = setInterval(fetchPendingCompanies, 120000);
    return () => clearInterval(interval);
  }, []);

  const fetchDetailedAnalysis = async (companyId) => {
    if (detailedAnalysis[companyId]) {
      // Already loaded
      setExpandedId(expandedId === companyId ? null : companyId);
      return;
    }

    setLoadingAnalysis(companyId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/companies/${companyId}/detailed-analysis`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der detaillierten Analyse');
      }
      
      const data = await response.json();
      setDetailedAnalysis(prev => ({
        ...prev,
        [companyId]: data.detailed_analysis
      }));
      setExpandedId(companyId);
    } catch (error) {
      console.error('Detailed analysis failed:', error);
      setError('❌ Detaillierte Analyse fehlgeschlagen');
    } finally {
      setLoadingAnalysis(null);
    }
  };

  const handleDecision = async (companyId, decision) => {
    try {
      setProcessingId(companyId);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/companies/${companyId}/decide?decision=${decision}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler bei der Verarbeitung');
      }

      const result = await response.json();
      console.log('Decision result:', result);

      // Remove the company from the list
      setPendingCompanies(prev => prev.filter(c => c.id !== companyId));
      setError(null);
      
      // Notify parent component to refresh
      if (onDecisionComplete) {
        onDecisionComplete();
      }
    } catch (err) {
      console.error('Decision error:', err);
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading && pendingCompanies.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Lade Unternehmen...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="mb-6 glass-card">
        <CardHeader className="py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h2 className="text-lg sm:text-xl font-bold text-white">
                Genehmigungswarteschlange
              </h2>
              <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-sm font-semibold border border-amber-500/30">
                {filterText.trim() ? `${visibleCompanies.length} von ${pendingCompanies.length}` : `${pendingCompanies.length} ausstehend`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchPendingCompanies}
                variant="outline"
                size="sm"
                disabled={loading}
                className="text-xs"
              >
                {loading ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : 'Aktualisieren'}
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="🔍 Suchen (Name, Stadt, Website)..."
              data-testid="pending-search-input"
              className="w-full px-3 py-2 rounded-md bg-slate-800/60 border border-slate-600 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </CardHeader>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              ⚠️ {error}
            </div>
          )}

          {pendingCompanies.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                  <p className="text-lg font-medium text-gray-700">
                    Keine ausstehenden Unternehmen
                  </p>
                  <p className="text-sm text-gray-500">
                    Alle Leads wurden bereits bearbeitet
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-2">
              {visibleCompanies.map((company) => (
                <Card key={company.id} className="overflow-hidden company-card hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="bg-gradient-to-r from-purple-900/30 via-indigo-900/30 to-blue-900/30 py-3 px-4 border-b border-purple-500/20">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base font-bold text-white mb-2">
                          {company.title}
                        </CardTitle>
                        
                        {/* Website - Prominent Display */}
                        <div className="mb-2">
                          <a
                            href={company.website?.startsWith('http') ? company.website : `https://${company.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 hover:text-blue-200 rounded-md text-sm font-medium transition-colors border border-blue-500/30"
                          >
                            <ExternalLink className="h-4 w-4 flex-shrink-0" />
                            <span className="break-all">
                              {company.website?.replace(/^https?:\/\/(www\.)?/, '') || company.website}
                            </span>
                          </a>
                        </div>
                        
                        {/* Address - Clickable Google Maps Link - City Only */}
                        {company.address && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-2 text-sm text-gray-300 bg-slate-800/40 hover:bg-slate-700/50 px-3 py-2 rounded-md cursor-pointer transition-colors border border-transparent hover:border-green-500/30"
                            title={company.address}
                          >
                            <span className="text-base flex-shrink-0">📍</span>
                            <span className="break-words font-medium">
                              {(() => {
                                // Extract city name from address
                                const match = company.address.match(/\d{5}\s+([A-Za-zäöüßÄÖÜ\s-]+)/);
                                if (match) return match[1].trim();
                                
                                // Fallback: Try to find known city names
                                const cities = ['Hamburg', 'Berlin', 'München', 'Bremen', 'Hannover', 'Kiel', 'Lübeck', 'Schwerin', 'Dresden', 'Leipzig', 'Köln', 'Frankfurt', 'Stuttgart', 'Düsseldorf', 'Dortmund'];
                                const foundCity = cities.find(city => company.address.includes(city));
                                return foundCity || company.address.substring(0, 35) + '...';
                              })()}
                            </span>
                          </a>
                        )}
                        
                        {/* Phone if available */}
                        {company.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-300 mt-2">
                            <span className="text-base">📞</span>
                            <span>{company.phone}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          onClick={() => fetchDetailedAnalysis(company.id)}
                          disabled={loadingAnalysis === company.id}
                          className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-2 py-1 h-8 text-xs shadow-md hover:shadow-lg transition-all duration-200"
                          size="sm"
                        >
                          {loadingAnalysis === company.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <FileText className="h-3 w-3" />
                              <span className="text-xs ml-1">{expandedId === company.id ? 'Ausblenden' : 'Details'}</span>
                            </>
                          )}
                        </Button>
                        {/* Only show approve/reject for admin and sub_admin */}
                        {currentUser && (currentUser.role === 'admin' || currentUser.role === 'sub_admin') && (
                          <>
                            <Button
                              onClick={() => handleDecision(company.id, 'approve')}
                              disabled={processingId === company.id}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-3 py-1 h-8 text-xs shadow-md hover:shadow-lg transition-all duration-200"
                              size="sm"
                              title="Genehmigen"
                            >
                              {processingId === company.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              onClick={() => handleDecision(company.id, 'reject')}
                              disabled={processingId === company.id}
                              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-3 py-1 h-8 text-xs shadow-md hover:shadow-lg transition-all duration-200"
                              size="sm"
                              title="Ablehnen"
                            >
                              {processingId === company.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {expandedId === company.id && detailedAnalysis[company.id] && (
                    <CardContent className="pt-2 pb-2 border-t border-purple-500/20">
                      <div className="bg-gradient-to-br from-blue-900/20 to-indigo-900/20 rounded p-2 border border-blue-500/20">
                        <div className="flex items-center gap-1 mb-1">
                          <FileText className="h-4 w-4 text-blue-400" />
                          <h3 className="text-xs font-bold text-white">Detaillierte Analyse</h3>
                        </div>
                        <div className="text-xs text-gray-200 whitespace-pre-wrap leading-relaxed">
                          {detailedAnalysis[company.id]}
                        </div>
                      </div>
                    </CardContent>
                  )}

                  {company.ai_analysis && (
                    <CardContent className="pt-2 pb-2">
                      <div className="bg-gray-800/50 rounded p-2 border border-purple-500/20">
                        <span className="text-xs font-semibold text-gray-400">AI: </span>
                        <span className="text-xs text-gray-200">{company.ai_analysis}</span>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
    </div>
  );
};

export default ApprovalQueue;
